import { Droplets, Shield, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Service } from "@/types";

const services: readonly Service[] = [
	{
		icon: Droplets,
		title: "Reinigen",
		image: "/cleaning-1837331_1280.jpg",
		description:
			"Außenreinigung, Innenraumreinigung, Felgenreinigung und auch Motorwäsche, mit uns bekommst du das, was dein Fahrzeug braucht.",
	},
	{
		icon: Sparkles,
		title: "Polieren",
		image: "/car-detailing-7223497_1280.jpg",
		description:
			"Wir polieren dein Fahrzeug je nach Zustand des Lackes in mehreren Schritten bis zum Finish.",
	},
	{
		icon: Shield,
		title: "Versiegeln",
		image: "/cleaning-1837333_1280.jpg",
		description:
			"Nano, Keramik oder auch Wachs, passend auf Dein Fahrzeug wählen wir gemeinsam das richtige Mittel, um Dein Fahrzeug zu schützen.",
	},
] as const;

const _additionalServices: readonly string[] = [
	"Außenreinigung",
	"Innenraumreinigung",
	"Felgenreinigung",
	"Motorwäsche",
	"Lackpolitur",
	"Keramikversiegelung",
	"Wachsversiegelung",
	"Nanotechnologie",
] as const;

const Services: React.FC = () => {
	const sectionRef = useRef<HTMLElement | null>(null);
	const [isVisible, setIsVisible] = useState<boolean>(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					setIsVisible(true);
					observer.unobserve(entry.target);
				}
			},
			{ threshold: 0.1 },
		);

		if (sectionRef.current) {
			observer.observe(sectionRef.current);
		}

		return () => observer.disconnect();
	}, []);

	return (
		<section
			ref={sectionRef}
			id="services"
			className="relative z-10 bg-black text-white py-32 overflow-hidden"
		>
			{/* Background Decorative Text */}
			<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[20vw] font-black text-white/[0.02] whitespace-nowrap pointer-events-none uppercase tracking-tighter select-none">
				Detailing Specialist
			</div>

			<div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
				{/* Section Header */}
				<div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8">
					<div className="max-w-2xl">
						<p className="text-[#cfc297] text-xs uppercase tracking-[0.5em] font-black mb-4">
							Professional Care
						</p>
						<h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.9]">
							Unsere <br />
							<span className="text-[#cfc297]">Expertise</span>
						</h2>
					</div>
					<p className="text-white/40 text-sm md:text-base max-w-sm font-light leading-relaxed uppercase tracking-widest">
						Wir bieten High-End Lösungen für den Lackschutz und die
						Werterhaltung Ihres Fahrzeugs.
					</p>
				</div>

				{/* Services Grid */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-12">
					{services.map((service, index) => (
						<div
							key={service.title}
							className={`group relative transition-all duration-1000 ${
								isVisible
									? "opacity-100 translate-y-0"
									: "opacity-0 translate-y-20"
							}`}
							style={{ transitionDelay: `${index * 150}ms` }}
						>
							{/* Image Container with Custom Interaction */}
							<div className="relative aspect-[4/5] overflow-hidden rounded-2xl mb-8 shadow-2xl">
								<img
									src={service.image}
									alt={service.title}
									className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale-[50%] group-hover:grayscale-0"
									loading="lazy"
								/>
								<div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />

								{/* Floating Icon */}
								<div className="absolute top-6 right-6 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 transition-transform duration-500 group-hover:rotate-[360deg] group-hover:bg-[#cfc297] group-hover:text-black">
									<service.icon className="w-5 h-5" />
								</div>

								<div className="absolute bottom-8 left-8 right-8">
									<h3 className="text-3xl font-black uppercase tracking-tighter mb-2 italic">
										{service.title}
									</h3>
									<div className="w-0 group-hover:w-full h-px bg-[#cfc297] transition-all duration-500" />
								</div>
							</div>

							<p className="text-white/50 text-sm leading-relaxed font-light tracking-wide px-2">
								{service.description}
							</p>
						</div>
					))}
				</div>

				{/* Additional Features Bar */}
				<div className="mt-32 pt-16 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-8">
					<div className="flex flex-col gap-2">
						<span className="text-[#cfc297] font-black text-2xl tracking-tighter uppercase">
							100%
						</span>
						<span className="text-white/30 text-[10px] uppercase tracking-[0.3em]">
							Handarbeit
						</span>
					</div>
					<div className="flex flex-col gap-2">
						<span className="text-[#cfc297] font-black text-2xl tracking-tighter uppercase">
							High-End
						</span>
						<span className="text-white/30 text-[10px] uppercase tracking-[0.3em]">
							Produkte
						</span>
					</div>
					<div className="flex flex-col gap-2">
						<span className="text-[#cfc297] font-black text-2xl tracking-tighter uppercase">
							Keramik
						</span>
						<span className="text-white/30 text-[10px] uppercase tracking-[0.3em]">
							Spezialist
						</span>
					</div>
					<div className="flex flex-col gap-2">
						<span className="text-[#cfc297] font-black text-2xl tracking-tighter uppercase">
							Premium
						</span>
						<span className="text-white/30 text-[10px] uppercase tracking-[0.3em]">
							Service
						</span>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Services;
